(() => {
  const scriptElement = document.currentScript;
  const closeLabel = scriptElement?.dataset.closeLabel?.trim() || "Close";
  const triggerSelector = "#sealapps-bis-widget .product-restore-email";
  const dialogSelector = "#email-me-frame";
  const controlSelector = [
    triggerSelector,
    `${dialogSelector} .close-box`,
    `${dialogSelector} .successSub_close-box`,
    `${dialogSelector} .email-me-submitButton`,
    `${dialogSelector} .email-type`,
    `${dialogSelector} .sms-type`,
  ].join(", ");
  const focusableSelector = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled]):not([type='hidden'])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "summary",
    "[contenteditable='true']",
    "[tabindex]:not([tabindex='-1'])",
  ].join(", ");

  let activeDialog = null;
  let dialogWasOpen = false;
  let focusReturnTarget = null;
  let frameRequested = false;
  let titleId = 0;
  let spaceControl = null;
  const observedStateRoots = new WeakSet();
  const stateObserver = new MutationObserver(scheduleSync);
  const structureObserver = new MutationObserver(scheduleSync);

  function setAttribute(element, name, value) {
    if (element?.getAttribute(name) !== value) {
      element?.setAttribute(name, value);
    }
  }

  function isVisible(element) {
    if (
      !element?.isConnected ||
      element.hidden ||
      element.closest("[hidden], [aria-hidden='true'], [inert]")
    ) {
      return false;
    }

    const styles = window.getComputedStyle(element);
    return (
      styles.display !== "none" &&
      styles.visibility !== "hidden" &&
      styles.opacity !== "0" &&
      element.getClientRects().length > 0
    );
  }

  function isNativeControl(element) {
    return element.matches("button, input, select, textarea, a[href], summary");
  }

  function isDisabled(element) {
    if (element.matches(":disabled, [disabled], [aria-disabled='true'], .disabled, .is-disabled")) {
      return true;
    }

    return window.getComputedStyle(element).pointerEvents === "none";
  }

  function makeButton(element, label) {
    if (!element) return;

    if (!isNativeControl(element)) {
      const needsKeyboardFallback =
        element.getAttribute("role") !== "button" || element.tabIndex < 0;
      setAttribute(element, "role", "button");
      setAttribute(element, "tabindex", "0");
      if (needsKeyboardFallback) {
        element.dataset.terrawagenCwillControl = "true";
      }
    }

    if (label && !element.hasAttribute("aria-label")) {
      setAttribute(element, "aria-label", label);
    }
  }

  function ensureTitleId(title) {
    if (title.id) return title.id;

    let candidate;
    do {
      titleId += 1;
      candidate = `TerrawagenCwillDialogTitle-${titleId}`;
    } while (document.getElementById(candidate));

    title.id = candidate;
    return candidate;
  }

  function visibleMatch(root, selectors) {
    return Array.from(root.querySelectorAll(selectors)).find(isVisible) || null;
  }

  function observeState(root) {
    if (!root || observedStateRoots.has(root)) return;

    stateObserver.observe(root, {
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "hidden", "aria-hidden"],
    });
    observedStateRoots.add(root);
  }

  function enhanceTriggers(dialogOpen) {
    document.querySelectorAll(triggerSelector).forEach((trigger) => {
      makeButton(trigger);
      // CWILL can supply role/tabindex itself, but its opener is the inner div.
      if (!isNativeControl(trigger) && trigger.querySelector(".email-me-button")) {
        trigger.dataset.terrawagenCwillControl = "true";
      }
      setAttribute(trigger, "aria-haspopup", "dialog");
      setAttribute(trigger, "aria-controls", "email-me-frame");
      setAttribute(trigger, "aria-expanded", String(dialogOpen));
    });
  }

  function enhanceDialog(dialog) {
    setAttribute(dialog, "role", "dialog");
    setAttribute(dialog, "aria-modal", "true");
    setAttribute(dialog, "tabindex", "-1");

    const title = visibleMatch(dialog, ".frame-title, .successSub_header_text");
    if (title) {
      setAttribute(dialog, "aria-labelledby", ensureTitleId(title));
    } else if (!dialog.hasAttribute("aria-label")) {
      const trigger = focusReturnTarget || document.querySelector(triggerSelector);
      const triggerLabel =
        trigger?.getAttribute("aria-label") || trigger?.textContent?.trim();
      setAttribute(dialog, "aria-label", triggerLabel || "Back in stock notification");
    }

    dialog.querySelectorAll(".close-box, .successSub_close-box").forEach((control) => {
      makeButton(control, closeLabel);
    });

    dialog.querySelectorAll(".email-me-submitButton").forEach((control) => {
      makeButton(control);
    });

    const toggler = dialog.querySelector(".notify-type-toggler");
    if (toggler) {
      toggler.querySelectorAll(".email-type, .sms-type").forEach((control) => {
        makeButton(control);
        setAttribute(control, "aria-pressed", String(control.classList.contains("type-selected")));
      });
    }

    dialog.querySelectorAll(".invalid-email-tips, .invalid-phone-tips").forEach((message) => {
      setAttribute(message, "role", "alert");
      setAttribute(message, "aria-live", "assertive");
      setAttribute(message, "aria-atomic", "true");
    });

    dialog.querySelectorAll(".successSub_text").forEach((message) => {
      setAttribute(message, "role", "status");
      setAttribute(message, "aria-live", "polite");
      setAttribute(message, "aria-atomic", "true");
    });
  }

  function getControlledCountrySurfaces(dialog) {
    if (!dialog?.querySelector(".buyer-phone")) return [];

    const ids = Array.from(
      dialog.querySelectorAll(
        ".iti [aria-controls], .iti [aria-owns], .intl-tel-input [aria-controls], .intl-tel-input [aria-owns]",
      ),
    ).flatMap((control) =>
      [control.getAttribute("aria-controls"), control.getAttribute("aria-owns")]
        .filter(Boolean)
        .flatMap((value) => value.trim().split(/\s+/)),
    );

    return ids.map((id) => document.getElementById(id)).filter(Boolean);
  }

  function isCountryPickerSurface(element, dialog = activeDialog) {
    if (!element || !dialog?.querySelector(".buyer-phone")) return false;

    const surface = element.closest(
      ".iti--container, .iti__dropdown-content, .iti__country-list",
    );
    if (!surface || !isVisible(surface)) return false;
    if (dialog.contains(surface)) return true;

    return getControlledCountrySurfaces(dialog).some(
      (controlled) =>
        controlled === surface || controlled.contains(element) || surface.contains(controlled),
    );
  }

  function hasOpenCountryPicker(dialog) {
    if (visibleMatch(dialog, ".iti__dropdown-content, .iti__country-list")) return true;

    return getControlledCountrySurfaces(dialog).some(isVisible);
  }

  function getFocusableElements(dialog) {
    return Array.from(dialog.querySelectorAll(focusableSelector)).filter(
      (element, index, all) =>
        all.indexOf(element) === index &&
        element.tabIndex >= 0 &&
        isVisible(element) &&
        !isDisabled(element),
    );
  }

  function focusDialog(dialog) {
    window.requestAnimationFrame(() => {
      if (activeDialog !== dialog || !isVisible(dialog)) return;
      if (
        dialog.contains(document.activeElement) ||
        isCountryPickerSurface(document.activeElement)
      ) {
        return;
      }

      dialog.focus({ preventScroll: true });
    });
  }

  function restoreFocus(previousDialog) {
    const savedTarget = focusReturnTarget;
    focusReturnTarget = null;

    window.requestAnimationFrame(() => {
      if (dialogWasOpen) return;

      const target =
        savedTarget?.isConnected && isVisible(savedTarget)
          ? savedTarget
          : Array.from(document.querySelectorAll(triggerSelector)).find(isVisible);
      if (!target) return;

      const focused = document.activeElement;
      if (
        !focused ||
        focused === document.body ||
        !focused.isConnected ||
        !isVisible(focused) ||
        previousDialog?.contains(focused) ||
        isCountryPickerSurface(focused, previousDialog)
      ) {
        target.focus({ preventScroll: true });
      }
    });
  }

  function sync() {
    frameRequested = false;

    const previousDialog = activeDialog;
    const previouslyOpen = dialogWasOpen;
    const dialog = document.querySelector(dialogSelector);
    const dialogOpen = Boolean(dialog && isVisible(dialog));

    enhanceTriggers(dialogOpen);
    document.querySelectorAll("#sealapps-bis-widget").forEach((widget) => {
      observeState(widget.closest(".product-info-secondary") || widget);
    });
    if (dialog) {
      observeState(dialog);
      enhanceDialog(dialog);
    }

    activeDialog = dialogOpen ? dialog : null;
    dialogWasOpen = dialogOpen;

    if (dialogOpen && (!previouslyOpen || previousDialog !== dialog)) {
      if (!focusReturnTarget) {
        const focusedTrigger = document.activeElement?.closest?.(triggerSelector);
        if (focusedTrigger) focusReturnTarget = focusedTrigger;
      }
      focusDialog(dialog);
    } else if (
      dialogOpen &&
      (document.activeElement === document.body || !document.activeElement?.isConnected)
    ) {
      focusDialog(dialog);
    } else if (previouslyOpen && !dialogOpen) {
      restoreFocus(previousDialog);
    }
  }

  function scheduleSync() {
    if (frameRequested) return;
    frameRequested = true;
    window.requestAnimationFrame(sync);
  }

  function clickControl(control) {
    if (control.matches(triggerSelector)) {
      const opener = control.querySelector(".email-me-button");
      if (opener && isVisible(opener) && !isDisabled(opener)) {
        opener.click();
      }
      return;
    }

    if (control.matches(".close-box")) {
      (control.querySelector(".frame-close") || control).click();
      return;
    }

    if (control.matches(".successSub_close-box")) {
      (control.querySelector(".successSub_frame-close") || control).click();
      return;
    }

    control.click();
  }

  function getEnhancedControl(target) {
    const control = target?.closest?.(controlSelector);
    if (!control || control.dataset.terrawagenCwillControl !== "true") return null;
    if (isNativeControl(control)) return null;
    // Leave any future native app button's own keyboard activation intact.
    if (target !== control && target?.closest?.("button, input, select, textarea, a[href]")) {
      return null;
    }
    return control;
  }

  function handleKeydown(event) {
    const control = getEnhancedControl(event.target);
    if (control && (event.key === "Enter" || event.key === " ")) {
      if (event.defaultPrevented) return;

      event.preventDefault();

      if (event.repeat || !isVisible(control) || isDisabled(control)) return;

      if (event.key === "Enter") {
        clickControl(control);
      } else {
        spaceControl = control;
      }
      return;
    }

    if (!activeDialog || !isVisible(activeDialog)) return;
    if (event.defaultPrevented) return;

    if (event.key === "Escape") {
      if (hasOpenCountryPicker(activeDialog)) return;

      const closeControl = visibleMatch(
        activeDialog,
        ".successSub_close-box, .close-box",
      );
      if (!closeControl || isDisabled(closeControl)) return;

      event.preventDefault();
      clickControl(closeControl);
      return;
    }

    if (event.key !== "Tab") return;
    if (isCountryPickerSurface(document.activeElement)) return;

    const focusable = getFocusableElements(activeDialog);
    if (!focusable.length) {
      event.preventDefault();
      activeDialog.focus({ preventScroll: true });
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const focused = document.activeElement;

    if (!focusable.includes(focused)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    } else if (event.shiftKey && focused === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && focused === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function handleKeyup(event) {
    if (event.key !== " " || !spaceControl) return;

    const control = spaceControl;
    spaceControl = null;
    if (event.defaultPrevented) return;

    event.preventDefault();

    if (document.activeElement === control && isVisible(control) && !isDisabled(control)) {
      clickControl(control);
    }
  }

  function handleClick(event) {
    const trigger = event.target?.closest?.(triggerSelector);
    if (trigger) focusReturnTarget = trigger;
  }

  function handleFocusIn(event) {
    if (!activeDialog || !isVisible(activeDialog)) return;
    if (activeDialog.contains(event.target) || isCountryPickerSurface(event.target)) return;

    activeDialog.focus({ preventScroll: true });
  }

  function init() {
    document.addEventListener("click", handleClick, true);
    document.addEventListener("keydown", handleKeydown);
    document.addEventListener("keyup", handleKeyup);
    document.addEventListener("focusin", handleFocusIn, true);
    document.addEventListener("shopify:section:load", scheduleSync);
    window.addEventListener("pageshow", scheduleSync);

    structureObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      subscribe(PUB_SUB_EVENTS.productVariantChange, scheduleSync);
      subscribe(PUB_SUB_EVENTS.sectionRefreshed, scheduleSync);
    }

    sync();
  }

  if (document.body) {
    init();
  } else {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  }
})();
